<template>
  <div id="overview">
    <app-header :tile-view-route="{
      name: 'project',
      params: { projectId: $route.params.projectId.toString() }
    }" :dashboard-view-route="{
        name: 'dashboard',
        params: { projectId: $route.params.projectId.toString(), dashboardId: '1' }
      }" />
    <aside class="asideExpanded">
      <h4 class="sidebar-title">{{ $t('pages.overview') }}</h4>
      <nav class="overview-nav" :aria-label="$t('pages.overview')">
        <ul class="overview-list">
          <li v-for="section in navSections" :key="section.titleKey" class="nav-section">
            <h5 class="nav-section-title">{{ $t(section.titleKey) }}</h5>
            <ul class="nav-section-items">
              <li v-for="item in section.items" :key="item.id">
                <router-link
                  :title="itemLabel(item)"
                  class="overview-link"
                  :aria-current="isCurrentOverview(item.id) ? 'page' : null"
                  :to="overviewLinkTo(item)">
                  <span class="overview-label">{{ itemLabel(item) }}</span>
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </aside>

    <div v-if="!$route.params.overviewId" class="no-overview">
      <span>{{ $t('label.selectOverview') }}</span>
    </div>
    <div v-else-if="fixedOverview" class="overview-tables">
      <component :is="fixedOverview.component" v-bind="fixedOverviewProps" />
    </div>
    <div v-else-if="overview && !isExcludedOverview" class="overview-tables">
      <div v-for="(table, key) in overview.tables" :key="key" class="overview-table" :style="{ height: tableHeight }">
        <div :is="table.name" :ref="table.name" :childTables="table.childTables"
          :project-id="parseInt($route.params.projectId)" :useMasterDetail="table.useMasterDetail"></div>
      </div>
    </div>
    <div v-else class="no-overview">
      <span>{{ $t('label.noOverview') }}</span>
    </div>
    <app-footer />
    <confirm-modal :show="showLeaveWarningModal" :title-label="$t('label.AreYouSure')"
      :confirm-button-label="$t('button.confirm')" :cancel-button-label="$t('button.cancel')"
      @close-modal="showLeaveWarningModal = false" @confirm-button-pressed="navigateToNext">
      {{ $t('message.unsavedChanges') }}
    </confirm-modal>
    <nv-loading :show="loading"></nv-loading>

    <ExportProjectPopup :exportPopupOpen="exportPopupOpen" @closeButtonClicked="toggleExportPopup" />
  </div>
</template>

<script>
import authenticationProviders from '@/utils/authenticationProvider'
import config from '@/configurations/config-environment'

import AppHeader from '@/components/_shared/header'
import AppFooter from '@/components/_shared/footer'
import TableCadastralOvam from '@/components/tables/tableCadastralOvam.vue'
import TableCadastralOvamVlaremAct from '@/components/tables/tableCadastralOvamVlaremAct'
import TableCadastralOvamCustomers from '@/components/tables/tableCadastralOvamCustomers'
import ConfirmModal from '@/components/modal/confirmModal'
import sqlOverview from '@/components/sqlOverview'
import TableAllSamplesWithResults from '@/components/tables/AnalysisResults/tableAllSamplesWithResults'
import nvLoading from '@/components/_shared/loading.vue'
import { bus } from '@/bus'
import _ from 'lodash'
import LocationVisitsTable from '@/components/dynamicTables/tables/locationVisitsTable'
import GroundWaterDataTableOverview from '@/components/table-overviews/groundWaterDataTableOverview'
import SoilDataTableOverview from '@/components/table-overviews/soilDataTableOverview'
import MeasurementPointsOverview from '@/components/table-overviews/measurementPointsOverview'
import SurfaceSamplesTableOverview from '@/components/table-overviews/surfaceSamplesTableOverview'
import GroundAirTableOverview from '@/components/table-overviews/groundAirTableOverview'
import ProjectParticipantsOverview from '@/components/table-overviews/projectParticipantsOverview'
import PictureGalleryOverview from '@/components/table-overviews/pictureGalleryOverview'
import AttachmentGalleryOverview from '../components/table-overviews/attachmentGalleryOverview'
import ManualAnalysesInput from '@/components/table-overviews/manualAnalysesInput.vue'
import ProjectOverview from '../components/table-overviews/projectOverview'
import AllLabAssignmentsOverview from '@/components/table-overviews/allLabAssignmentsOverview.vue'
import ExportProjectPopup from '@/components/export/exportProjectPopup.vue'

// import { TestOverview } from '../components/betterTableOverviews/overviews'

const authenticationProvider = authenticationProviders[config.platform].default

export default {
  name: 'Overview',
  components: {
    nvLoading,
    AppHeader,
    AppFooter,
    TableCadastralOvam,
    TableCadastralOvamVlaremAct,
    TableCadastralOvamCustomers,
    ConfirmModal,
    sqlOverview,
    TableAllSamplesWithResults,
    LocationVisitsTable,
    GroundWaterDataTableOverview,
    SoilDataTableOverview,
    MeasurementPointsOverview,
    SurfaceSamplesTableOverview,
    GroundAirTableOverview,
    ProjectParticipantsOverview,
    PictureGalleryOverview,
    AttachmentGalleryOverview,
    ProjectOverview,
    AllLabAssignmentsOverview,
    ManualAnalysesInput,
    ExportProjectPopup
    // TestOverview
  },
  data() {
    return {
      showLeaveWarningModal: false,
      next: null,
      overviews: [
        // An array of all available overviews
        {
          order: 4, // The position the item will have in the left menu bar
          id: 1, // Overview ID, this is also the route where the overview will be available
          name: 'OVAM', // The full name of the overview, this will be displayed when the left menu is expanded
          key: 'OV', // The key of the overview, this is displayed in the icon in the left menu
          tables: [
            {
              name: 'tableCadastralOvam',
              childTables: ['tableCadastralOvamVlaremAct', 'tableCadastralOvamCustomers']
            },
            {
              name: 'tableCadastralOvamVlaremAct'
            },
            {
              name: 'tableCadastralOvamCustomers'
            }
          ], // An array of all the tables displayed in the overview. The names must match the names of the table component /components/tables/
          tileViewRoute: {
            name: 'project',
            params: { projectId: this.$route.params.projectId.toString() }
          } // The location object where the tile button should return to.
        }
      ],
      fixedOverviews: {
        99: { component: 'sqlOverview' },
        100: { component: 'TableAllSamplesWithResults', projectIdProp: 'project-id' },
        101: { component: 'LocationVisitsTable', projectIdProp: 'projectId' },
        102: { component: 'SoilDataTableOverview' },
        103: { component: 'GroundWaterDataTableOverview', projectIdProp: 'projectId' },
        104: { component: 'MeasurementPointsOverview' },
        105: { component: 'SurfaceSamplesTableOverview' },
        106: { component: 'GroundAirTableOverview' },
        107: { component: 'ProjectParticipantsOverview' },
        108: { component: 'ProjectOverview' },
        110: { component: 'PictureGalleryOverview' },
        111: { component: 'AttachmentGalleryOverview' },
        113: { component: 'ManualAnalysesInput', projectIdProp: 'projectId' },
        114: { component: 'AllLabAssignmentsOverview' }
        // 112: { component: 'TestOverview' }
      },
      excludedOverViewIDS: ['99', '100', '101'],
      exportPopupOpen: false
    }
  },
  computed: {
    loading: function () {
      return this.$store.getters.isLoading
    },
    /* Flag to access controll the overviews button */
    allowOverviews: function () {
      return authenticationProvider.getAuthenticatedUser().language === 'nl'
    },
    /* The currently selected overview */
    overview: function () {
      return this.overviews.find((overview) => overview.id.toString() === this.$route.params.overviewId.toString())
    },
    orderedOverviews: function () {
      return _.orderBy(this.overviews, 'order')
    },
    navSections() {
      return [
        {
          titleKey: 'overviewNav.project',
          items: [
            { id: '108', labelKey: 'ProjectOverview.PageTitle' },
            { id: '101', labelKey: 'LocationVisits.PageTitle' },
            { id: '107', labelKey: 'ProjectParticipantsOverview.PageTitle' },
            ...this.orderedOverviews.map((overview) => ({
              id: overview.id.toString(),
              label: overview.name
            }))
          ]
        },
        {
          titleKey: 'overviewNav.fieldData',
          items: [
            { id: '104', labelKey: 'MeasurementPointsOverview.PageTitle', withMpGuid: true },
            { id: '102', labelKey: 'SoilDataTableOverview.PageTitle', withMpGuid: true },
            { id: '103', labelKey: 'GroundWaterDataTableOverview.PageTitle', withMpGuid: true },
            { id: '106', labelKey: 'GroundAirTableOverview.PageTitle', withMpGuid: true },
            { id: '105', labelKey: 'SurfaceSamplesTableOverview.PageTitle', withMpGuid: true }
          ]
        },
        {
          titleKey: 'overviewNav.analyses',
          items: [
            { id: '100', labelKey: 'AnalysesResults.PageTitle' },
            { id: '113', labelKey: 'manualAnalysisResultInput.buttons.title' }
          ]
        },
        {
          titleKey: 'overviewNav.attachments',
          items: [
            { id: '110', labelKey: 'PictureGalleryOverview.PageTitle' },
            { id: '111', labelKey: 'AttachmentGalleryOverview.PageTitle' }
          ]
        },
        {
          titleKey: 'tableTitles.AllProjects',
          items: [
            { id: '114', labelKey: 'AllLabassignments.PageTitle' },
            ...(this.allowOverviews ? [{ id: '99', label: 'Overzicht' }] : [])
          ]
        }
      ]
    },
    tableHeight: function () {
      return 100 / this.overview.tables.length + '%'
    },
    selected: {
      get() {
        return this.$store.state.selected
      },
      set(value) {
        this.$store.commit('setSelected', value)
      }
    },
    isExcludedOverview() {
      return this.excludedOverViewIDS.includes(this.$route.params.overviewId)
    },
    mpGuid() {
      return this.$route.params.mpGuid
    },
    projectId() {
      return this.$route.params.projectId
    },
    fixedOverview() {
      return this.fixedOverviews[this.$route.params.overviewId] || null
    },
    fixedOverviewProps() {
      if (!this.fixedOverview || !this.fixedOverview.projectIdProp) {
        return {}
      }
      return { [this.fixedOverview.projectIdProp]: this.projectId }
    }
  },
  methods: {
    /** Drives aria-current; the visual active state comes from router-link-exact-active. */
    isCurrentOverview(overviewId) {
      return String(this.$route.params.overviewId) === String(overviewId)
    },
    itemLabel(item) {
      return item.label || this.$t(item.labelKey)
    },
    overviewLinkTo(item) {
      const params = { overviewId: item.id.toString() }
      if (item.withMpGuid) {
        params.mpGuid = this.mpGuid
      }
      return { name: 'overview', params }
    },
    async xyzImport() {
      this.$router.push({
        name: 'ExternalXYZ',
        params: {
          PrID: this.projectId
        }
      })
    },

    /* Navigates to the route stored in this.next */
    navigateToNext() {
      if (this.next) {
        this.$store.commit('setSelected', {})
        this.next()
      }
    },

    /* Loops trough all tables currently displayed and returns true if one more of the tables have unsaved changes */
    checkIfTablesHaveChanges() {
      let unsavedChanges = false
      if (!this.isExcludedOverview) {
        if (this.overview) {
          this.overview.tables.forEach((table) => {
            if (this.$refs[table.name] && this.$refs[table.name][0] && this.$refs[table.name][0].$refs.tableBase) {
              if (this.$refs[table.name][0].$refs.tableBase.hasChanges()) {
                unsavedChanges = true
              }
            }
          })
        }
      }
      return unsavedChanges
    },
    async toggleExportPopup() {
      if (!this.exportPopupOpen && this.$store.state.workingObjectChanged) {
        await this.save()
      }
      this.exportPopupOpen = !this.exportPopupOpen
    }
  },

  created() {
    this.$store
      .dispatch('fetchProject', {
        PrID: this.$route.params.projectId
      })
      .then(() => {
        this.$store.state.activeTitle = this.$store.state.currentProject.PrCode
      })
    window.onbeforeunload = () => {
      if (this.checkIfTablesHaveChanges()) {
        return confirm(this.$t('message.confirm_exit'))
      }
    }
    bus.$on('updateSelected', (selected) => {
      this.$set(this.selected, selected.key, selected.data)
      // this.selected = { ...this.selected, [selected.key]: selected.data }
    })
    bus.$on('removeSelected', (key) => {
      this.$delete(this.selected, key)
    })
  },
  /* Make sure that user confirms navigating away when there are unsaved changes. Do this when the route is left (navigating away from /overviews) and when the route updates (changing the overview) */
  beforeRouteLeave(to, from, next) {
    if (this.isExcludedOverview) {
      this.navigateToNext()
    }
    let unsavedChanges = this.checkIfTablesHaveChanges()
    this.next = next
    if (unsavedChanges) {
      this.showLeaveWarningModal = true
      next(false) // This is done to resolve the hook
    } else {
      this.navigateToNext()
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (this.isExcludedOverview) {
      next()
    }
    let unsavedChanges = this.checkIfTablesHaveChanges()
    if (unsavedChanges) {
      this.next = next
      this.showLeaveWarningModal = true
    } else {
      next()
    }
  }
}
</script>

<style lang="less" scoped>
#overview {
  position: relative;
  height: 100%;
  overflow: hidden;

  aside {
    height: 100%;
    overflow: hidden;

    .expand {
      position: absolute;
      right: -0.75rem;
      top: 0.5rem;
      width: 1.5rem;
      height: 1.5rem;
      text-align: center;
      border-radius: 1.5rem;
      color: @white;
      background-color: @primaryGreen;
      box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
      cursor: pointer;
      line-height: 1.5rem;
      transition: transform 0.2s ease-out;
      transform: rotate(0deg);
    }

    .rotated {
      transform: rotate(180deg) translateY(-1px);
    }
  }

  .asideExpanded {
    display: flex;
    flex-direction: column;
    width: 18rem;
    float: left;
    z-index: 10;
    background-color: #515151;
    overflow: hidden;
    padding: 5px 5px;
  }

  .sidebar-title {
    margin: 0;
    padding: 0.75rem 0.75rem 0.5rem;
    color: @white;
    font-size: 20px;
    font-weight: 600;
  }

  .overview-nav {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .overview-list {
    list-style: none;
    margin: 0;
    padding: 0 0.5rem 0.5rem;
  }

  .nav-section+.nav-section {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
  }

  .nav-section-title {
    margin: 0 0 0.4rem;
    padding: 0 0.5rem;
    color: @gray300;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .nav-section-items {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .overview-link {
    display: block;
    border-left: 3px solid transparent;
    border-radius: 3px;
    padding: 0.45rem 0.5rem 0.45rem 0.6rem;
    color: @gray300;
    font-size: 1.05rem;
    text-decoration: none;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover,
    &:focus-visible {
      background-color: rgba(255, 255, 255, 0.08);
      color: @white;
      text-decoration: none;
    }

    &:focus-visible {
      outline: 2px solid @primaryGreen;
      outline-offset: -2px;
    }

    &:active,
    &:visited {
      text-decoration: none;
    }
  }

  .overview-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .overview-link.router-link-exact-active {
    background-color: rgba(255, 255, 255, 0.14);
    border-left-color: @primaryGreen;
    color: @white;
    font-weight: 600;
  }

  .no-overview {
    margin-left: auto;
    margin-right: auto;
    width: 500px;
    padding-top: 50px;
    text-align: center;

    span {
      font-size: 1.5em;
      color: #717171;
    }
  }

  .overview-tables {
    padding-top: 0.7rem;
    padding-right: 0.7rem;
    padding-bottom: 0.7rem;
    padding-left: 4.7rem;
    height: 100%;

    .overview-table {
      overflow: hidden;

      >div {
        height: 100%;
      }
    }
  }
}

.dx-datagrid .dx-row>td {
  padding-top: 0px !important;
  padding-left: 5px !important;
  padding-right: 5px !important;
  padding-bottom: 2px !important;
}
</style>
